// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useMemo } from 'react';

import type { SurveyType } from '../../api/types';
import { blockReasons, blockingItems, heldBlockingIds } from '../../bygning/blocking';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { PartBar } from '../../components/bygning/PartBar';
import { TypeInspector } from '../../components/bygning/inspector/TypeInspector';
import { useReview } from '../../components/bygning/inspector/useReview';
import { BlockingList } from '../../components/bygning/rapport/BlockingList';
import type { ModeComponent, ModeView } from './modeTypes';
import { NoSurvey, evidenceCentre, useInspectorKeys } from './RessourcerMode';
import ressourcer from './RessourcerMode.module.css';
import styles from './GennemgangMode.module.css';

/**
 * Gennemgang — "get everything unblocked": what keeps the report a kladde,
 * worked through one type at a time.
 *
 *  - left: "Det, der blokerer" (`bygning/blocking.ts`), every blocking type
 *    with its reasons — an open sample, no approval, no tons — the one in the
 *    inspector marked; the shell's J/K walk it (on a phone, one bar that
 *    scrolls sideways);
 *  - right: the type in the inspector's review variant (photo evidence from
 *    the parts' best frames above the fields; Godkend/Fejl for a queued type,
 *    Tilbage i kø for an approved one that blocks on a sample or tons);
 *  - centre: the evidence tile (`?ev=`), beside the list.
 *
 * The legend by status floats over the model (the Ground draws it). When
 * nothing blocks: "Intet blokerer", and the way on to Rapporter &
 * indberetning.
 */
export const GennemgangMode: ModeComponent = (props) => {
  const { data, sel, setSel, actions } = props;
  const items = useMemo(() => blockingItems(data.types, data.samples, data.fractions), [data.types, data.samples, data.fractions]);
  const held = useMemo(() => heldBlockingIds(items), [items]);
  // The client's own reading, so a type leaves the list (and the selection moves on) as soon as it is fixed.
  const isBlocking = useCallback((t: SurveyType) => blockReasons(t, data.samples).length > 0, [data.samples]);
  const onReview = useReview(data, actions, setSel, isBlocking);

  // The selected type while it still blocks (or just fixed, until the write lands), else the first in the list.
  const selected = data.types.find((t) => t.id === sel.typeId) ?? null;
  const first = held.length > 0 ? (data.types.find((t) => t.id === held[0]) ?? null) : null;
  const type = selected && (held.includes(selected.id) || isBlocking(selected) || data.busy) ? selected : first;
  useInspectorKeys(props, type);

  // Loaded, not loading, and still null: the read failed.
  const fractionsFailed = data.fractions === null && data.loaded && !data.loading;
  const empty = data.loaded && data.types.length === 0;
  const done = data.loaded && !empty && items.length === 0;

  const view: ModeView = {
    leftPhoneBar: true,
    leftWithCentre: true,
    left: empty ? undefined : (
      <div className={styles.left}>
        <BlockingList
          items={items}
          activeTypeId={type?.id ?? null}
          onOpenType={(id) => setSel({ typeId: id })}
          phoneBar
          failed={fractionsFailed}
          onRetry={actions.refresh}
        />
      </div>
    ),
    right: empty ? (
      <NoSurvey data={data} actions={actions} />
    ) : done ? (
      <div className={ressourcer.empty}>
        <EmptyState
          bare
          title="Intet blokerer"
          detail="Alle typer er godkendt og har tons og prøvesvar. Generér en ny rapportversion under Rapporter & indberetning."
          action={
            <Button size="lg" onClick={() => setSel({ mode: 'rapport' })}>
              Gå til Rapporter
            </Button>
          }
        />
      </div>
    ) : type ? (
      <>
        <TypeInspector
          type={type}
          data={data}
          sel={sel}
          setSel={setSel}
          actions={actions}
          variant="review"
          onReview={(status) => onReview(type, status)}
        />
        <PartBar types={data.types} dismissSupported={data.dismissSupported} sel={sel} setSel={setSel} actions={actions} busy={data.busy} />
      </>
    ) : undefined,
  };
  const evidence = evidenceCentre(props, type);
  if (evidence) view.centre = evidence;
  return view;
};
